import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import type { CatalogResponse } from '@nadoutong/contracts';
import { Link } from 'react-router-dom';
import { getGatewayBaseUrl } from '../../api/client.ts';
import { useCatalog } from '../../api/useCatalog.ts';
import { coerceErrorMessage, useToast } from '../../components/Toast.tsx';
import { useT } from '../../i18n/index.ts';
import type { I18nKey } from '../../i18n/index.ts';
import {
  Button,
  Icon,
  PageHeader,
  Panel,
  StatusLamp,
  TBody,
  Table,
  Tabs,
  TabsList,
  TabsTrigger,
  Td,
  Th,
  THead,
  Tr,
  buttonClass,
  cx,
} from '../../ui/index.ts';
import {
  CC_SWITCH_INSTALL_SNIPPET,
  CLAUDE_CODE_INSTALL_SNIPPET,
  ENV_CHECK_SNIPPET,
  EXAMPLE_ANTHROPIC_MODEL,
  EXAMPLE_IMAGE_MODEL,
  EXAMPLE_OPENAI_MODEL,
  FALLBACK_ANTHROPIC_MODELS,
  FALLBACK_OPENAI_MODELS,
  anthropicCurlSnippet,
  claudeLongTermSnippet,
  claudeTempEnvSnippet,
  codexConfigSnippet,
  codexEnvSnippet,
  disableClaudeBetasSnippet,
  imagesCurlBothSnippet,
  imagesSdkSnippet,
  lobechatEnvSnippet,
  modelsCurlSnippet,
  openaiCurlSnippet,
  openclawProviderSnippet,
} from './docsSnippets.ts';
import type { DocsPlatform, DocsProtocol } from './docsSnippets.ts';

/**
 * `/access/docs` — the integration docs page (epic #897 PR 5; rewritten
 * for the SKU marketplace naming model of epic #898).
 *
 * Absorbs the base-URL + quickstart cards that used to sit on the Keys
 * page, so the gateway base URL now has exactly one home in the
 * console. All prose is served from the i18n dictionaries
 * (`accessDocs.*`); code snippets stay code and live in
 * `docsSnippets.ts` together with the illustrative model literals.
 * Model names in prose and snippets come from the live catalog SKU
 * market when available and always use the canonical `namespace/model`
 * form — bare names only resolve through per-key model mappings.
 *
 * Layout: sticky section TOC on xl; below xl the TOC renders as a
 * horizontal anchor chip row at the top instead of disappearing.
 */

type SectionId =
  | 'prepare'
  | 'models'
  | 'create-key'
  | 'claude-code'
  | 'codex'
  | 'cc-switch'
  | 'openclaw'
  | 'clients'
  | 'claude-desktop'
  | 'vscode'
  | 'images'
  | 'verify';

const SECTIONS: readonly { readonly id: SectionId; readonly labelKey: I18nKey }[] = [
  { id: 'prepare', labelKey: 'accessDocs.section.prepare.label' },
  { id: 'models', labelKey: 'accessDocs.section.models.label' },
  { id: 'create-key', labelKey: 'accessDocs.section.createKey.label' },
  { id: 'claude-code', labelKey: 'accessDocs.section.claudeCode.label' },
  { id: 'codex', labelKey: 'accessDocs.section.codex.label' },
  { id: 'cc-switch', labelKey: 'accessDocs.section.ccSwitch.label' },
  { id: 'openclaw', labelKey: 'accessDocs.section.openclaw.label' },
  { id: 'clients', labelKey: 'accessDocs.section.clients.label' },
  { id: 'claude-desktop', labelKey: 'accessDocs.section.claudeDesktop.label' },
  { id: 'vscode', labelKey: 'accessDocs.section.vscode.label' },
  { id: 'images', labelKey: 'accessDocs.section.images.label' },
  { id: 'verify', labelKey: 'accessDocs.section.verify.label' },
];

/**
 * Which section the reader is currently looking at, for the TOC's active
 * state. Both TOCs list every section at once, so without this there is
 * nothing tying an entry to what is actually on screen.
 *
 * `IntersectionObserver` against the viewport (`root: null`), deliberately: it
 * stays correct whether the page scrolls at the window or inside a shell
 * container, which a scroll listener would have to guess.
 *
 * `rootMargin` narrows the observation to a strip near the top, so "current"
 * means "the heading you just scrolled under". With a full-viewport band
 * several sections qualify at once and the highlight stops meaning anything.
 *
 * Active is the LAST intersecting section in document order, not the first.
 * Both are in the strip during the handover between two sections, and the one
 * the reader has arrived at is the later one — its heading is what just
 * crossed the top line. Taking the first instead lags a whole section behind,
 * which is what a browser check caught: scrolling to `#codex` lit `claude-code`.
 *
 * When the strip holds nothing — at the very bottom, where the last section's
 * top is already above it — the previous value stands rather than clearing, so
 * the highlight never blinks off mid-read.
 *
 * Feature-detected: happy-dom (the test DOM) has no IntersectionObserver, and
 * a missing highlight must not take the page down with it.
 */
function useActiveSection(): SectionId | null {
  const [active, setActive] = useState<SectionId | null>(null);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;

    const intersecting = new Set<string>();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) intersecting.add(entry.target.id);
          else intersecting.delete(entry.target.id);
        }
        let current: SectionId | null = null;
        for (const section of SECTIONS) {
          if (intersecting.has(section.id)) current = section.id;
        }
        if (current !== null) setActive(current);
      },
      { rootMargin: '-72px 0px -68% 0px', threshold: 0 },
    );

    for (const section of SECTIONS) {
      const el = document.getElementById(section.id);
      if (el !== null) observer.observe(el);
    }
    return () => {
      observer.disconnect();
    };
  }, []);

  return active;
}

/**
 * What the Images routes do with each parameter they accept.
 *
 * Written from measurement, not from the OpenAI reference: several parameters
 * are accepted and then not honoured on this route, and a table that repeated
 * the reference would be documenting a different service.
 *
 * Split per route rather than one merged table with a "which route" column:
 * the two share most parameters but differ on the ones that decide whether a
 * request is even valid (`image` is required on one and rejected on the
 * other), and a merged table makes the reader resolve that per row.
 *
 * `required` and `default` are shown for every row. A parameter with no
 * default says so explicitly — an empty cell reads as "not documented" rather
 * than "there isn't one".
 *
 * Only defaults THIS service decides are stated. `quality`, `background` and
 * `output_format` are settled upstream when omitted, so their default is
 * listed as none: printing upstream's current pick would be publishing a
 * number nobody here can hold, and upstream has changed such picks before.
 */
interface ImageParamRow {
  readonly param: string;
  /** Omitted renders as the dictionary's "none" label, never a blank cell. */
  readonly defaultValue?: string;
  readonly required: boolean;
  readonly behaviourKey: I18nKey;
}

/**
 * Shared by both routes; `image` / `mask` are spliced in for `edits`.
 *
 * `n`, `response_format` and `background` accept exactly one value each and
 * `quality` is accepted then ignored — so as knobs they are worthless, and
 * dropping them from the table was tried. They stay because the rejection a
 * tenant gets for setting one is the shared sanitized `REQUEST_NOT_SUPPORTED`
 * body with a null `param`: nothing in the response says which field was the
 * problem. Someone copying OpenAI sample code, which routinely sets `n` and
 * `response_format`, would face a 400 with no way to find the cause. The row
 * is the only place that answer exists.
 */
const IMAGE_COMMON_PARAMS: readonly ImageParamRow[] = [
  // Absent and `auto` are the same request to this route: neither constrains
  // the aspect ratio. That is our own handling, so it is a default we own.
  { param: 'size', required: false, defaultValue: 'auto', behaviourKey: 'accessDocs.images.param.size' },
  { param: 'quality', required: false, behaviourKey: 'accessDocs.images.param.quality' },
  { param: 'n', required: false, defaultValue: '1', behaviourKey: 'accessDocs.images.param.n' },
  {
    param: 'response_format',
    required: false,
    defaultValue: 'b64_json',
    behaviourKey: 'accessDocs.images.param.response_format',
  },
  { param: 'background', required: false, behaviourKey: 'accessDocs.images.param.background' },
  {
    param: 'output_format',
    required: false,
    behaviourKey: 'accessDocs.images.param.output_format',
  },
  {
    param: 'output_compression',
    required: false,
    behaviourKey: 'accessDocs.images.param.output_compression',
  },
];

const IMAGE_GENERATE_PARAMS: readonly ImageParamRow[] = [
  { param: 'model', required: true, behaviourKey: 'accessDocs.images.param.model' },
  { param: 'prompt', required: true, behaviourKey: 'accessDocs.images.param.prompt' },
  ...IMAGE_COMMON_PARAMS,
];

const IMAGE_EDIT_PARAMS: readonly ImageParamRow[] = [
  { param: 'model', required: true, behaviourKey: 'accessDocs.images.param.model' },
  { param: 'prompt', required: true, behaviourKey: 'accessDocs.images.param.prompt' },
  { param: 'image', required: true, behaviourKey: 'accessDocs.images.param.image' },
  { param: 'mask', required: false, behaviourKey: 'accessDocs.images.param.mask' },
  ...IMAGE_COMMON_PARAMS,
];

interface ToolGuideRow {
  readonly name: string;
  readonly supplier: 'anthropic' | 'openai' | 'mixed';
  readonly base: 'anthropic' | 'openai' | 'both';
  readonly detailKey: I18nKey;
}

const TOOL_GUIDES: readonly ToolGuideRow[] = [
  { name: 'Claude Code', supplier: 'anthropic', base: 'anthropic', detailKey: 'accessDocs.clients.tool.claude_code' },
  { name: 'Codex', supplier: 'openai', base: 'openai', detailKey: 'accessDocs.clients.tool.codex' },
  { name: 'CC Switch', supplier: 'mixed', base: 'both', detailKey: 'accessDocs.clients.tool.cc_switch' },
  { name: 'OpenClaw', supplier: 'openai', base: 'openai', detailKey: 'accessDocs.clients.tool.openclaw' },
  { name: 'Cherry Studio', supplier: 'openai', base: 'openai', detailKey: 'accessDocs.clients.tool.cherry_studio' },
  { name: 'ChatBox', supplier: 'openai', base: 'openai', detailKey: 'accessDocs.clients.tool.chatbox' },
  { name: 'LobeChat', supplier: 'openai', base: 'openai', detailKey: 'accessDocs.clients.tool.lobechat' },
  { name: 'Claude Desktop', supplier: 'anthropic', base: 'anthropic', detailKey: 'accessDocs.clients.tool.claude_desktop' },
];

interface ClientGuide {
  readonly name: string;
  readonly keyName: string;
  readonly entryKey: I18nKey;
  readonly testKey: I18nKey;
  readonly noteKey: I18nKey;
}

const CLIENT_GUIDES: readonly ClientGuide[] = [
  {
    name: 'Cherry Studio',
    keyName: 'cherry-studio',
    entryKey: 'accessDocs.clients.cherry.entry',
    testKey: 'accessDocs.clients.cherry.test',
    noteKey: 'accessDocs.clients.cherry.note',
  },
  {
    name: 'ChatBox',
    keyName: 'chatbox',
    entryKey: 'accessDocs.clients.chatbox.entry',
    testKey: 'accessDocs.clients.chatbox.test',
    noteKey: 'accessDocs.clients.chatbox.note',
  },
  {
    name: 'LobeChat',
    keyName: 'lobechat',
    entryKey: 'accessDocs.clients.lobechat.entry',
    testKey: 'accessDocs.clients.lobechat.test',
    noteKey: 'accessDocs.clients.lobechat.note',
  },
];

const VERIFY_ERRORS: readonly {
  readonly symptomKey: I18nKey;
  readonly reasonKey: I18nKey;
  readonly actionKey: I18nKey;
}[] = [
  {
    symptomKey: 'accessDocs.verify.error1.symptom',
    reasonKey: 'accessDocs.verify.error1.reason',
    actionKey: 'accessDocs.verify.error1.action',
  },
  {
    symptomKey: 'accessDocs.verify.error2.symptom',
    reasonKey: 'accessDocs.verify.error2.reason',
    actionKey: 'accessDocs.verify.error2.action',
  },
  {
    symptomKey: 'accessDocs.verify.error3.symptom',
    reasonKey: 'accessDocs.verify.error3.reason',
    actionKey: 'accessDocs.verify.error3.action',
  },
  {
    symptomKey: 'accessDocs.verify.error4.symptom',
    reasonKey: 'accessDocs.verify.error4.reason',
    actionKey: 'accessDocs.verify.error4.action',
  },
  {
    symptomKey: 'accessDocs.verify.error5.symptom',
    reasonKey: 'accessDocs.verify.error5.reason',
    actionKey: 'accessDocs.verify.error5.action',
  },
  {
    symptomKey: 'accessDocs.verify.error6.symptom',
    reasonKey: 'accessDocs.verify.error6.reason',
    actionKey: 'accessDocs.verify.error6.action',
  },
  {
    symptomKey: 'accessDocs.verify.error7.symptom',
    reasonKey: 'accessDocs.verify.error7.reason',
    actionKey: 'accessDocs.verify.error7.action',
  },
];

/**
 * Canonical `namespace/model` names for one vendor, from the catalog
 * SKU market (epic #898). Null while the catalog has no data so the
 * caller can fall back to the illustrative literals.
 */
function vendorSkuNames(
  catalog: CatalogResponse | undefined,
  vendor: string,
): readonly string[] | null {
  if (catalog?.skus === undefined) return null;
  const names = catalog.skus.filter((sku) => sku.vendor === vendor).map((sku) => sku.sku_name);
  return names.length > 0 ? names : null;
}

export function AccessDocs(): JSX.Element {
  const t = useT();
  const toast = useToast();
  const catalogQ = useCatalog();
  const [platform, setPlatform] = useState<DocsPlatform>('macos');
  const [protocol, setProtocol] = useState<DocsProtocol>('anthropic');
  const activeSection = useActiveSection();

  const anthropicBase = getGatewayBaseUrl();
  const openaiBase = `${anthropicBase}/v1`;
  const consoleOrigin = typeof window !== 'undefined' ? window.location.origin : '';

  const anthropicSkuNames = vendorSkuNames(catalogQ.data, 'anthropic') ?? FALLBACK_ANTHROPIC_MODELS;
  const openaiSkuNames = vendorSkuNames(catalogQ.data, 'openai') ?? FALLBACK_OPENAI_MODELS;
  const claudeModels = anthropicSkuNames.join(' / ');
  const gptModels = openaiSkuNames.join(' / ');
  const exampleAnthropicModel = anthropicSkuNames[0] ?? EXAMPLE_ANTHROPIC_MODEL;
  const exampleOpenaiModel = openaiSkuNames[0] ?? EXAMPLE_OPENAI_MODEL;
  // The catalog carries no image marker a tenant key can read — the metering
  // profile is upstream policy and deliberately invisible — so the example
  // falls back to the name, in the conservative direction: a wrong guess shows
  // an illustrative literal, never a text SKU presented as an image one.
  const exampleImageModel =
    openaiSkuNames.find((name) => name.toLowerCase().includes('image')) ?? EXAMPLE_IMAGE_MODEL;

  async function copy(text: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t('accessDocs.toast.copied'));
    } catch (err) {
      toast.error(coerceErrorMessage(err));
    }
  }

  const onCopy = (text: string) => () => {
    void copy(text);
  };

  return (
    <section data-testid="access-docs-page" className="flex flex-col gap-4">
      <PageHeader
        title={t('accessDocs.title')}
        sub={t('accessDocs.subtitle')}
        testId="access-docs-header"
      />

      {/* The base URL's ONE home in the console (absorbed from /keys). */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-3" data-testid="docs-fact-cards">
        <FactCard
          title={t('accessDocs.fact.anthropic.title')}
          note={t('accessDocs.fact.anthropic.note')}
          noteTone="warn"
          value={anthropicBase}
          copyLabel={t('accessDocs.fact.copy')}
          onCopy={onCopy(anthropicBase)}
          testId="docs-fact-anthropic"
        />
        <FactCard
          title={t('accessDocs.fact.openai.title')}
          note={t('accessDocs.fact.openai.note')}
          noteTone="signal"
          value={openaiBase}
          copyLabel={t('accessDocs.fact.copy')}
          onCopy={onCopy(openaiBase)}
          testId="docs-fact-openai"
        />
        <FactCard
          title={t('accessDocs.fact.auth.title')}
          note={t('accessDocs.fact.auth.note')}
          value="Authorization: Bearer sk-ndt-…"
          testId="docs-fact-auth"
        />
      </div>

      {/* Below xl: TOC as a horizontal anchor chip row. */}
      <nav
        aria-label={t('accessDocs.toc.aria')}
        data-testid="docs-toc-chips"
        className="flex flex-wrap gap-1.5 xl:hidden"
      >
        {SECTIONS.map((section) => {
          const isActive = section.id === activeSection;
          return (
            <a
              key={section.id}
              href={`#${section.id}`}
              aria-current={isActive ? 'true' : undefined}
              data-testid={`docs-toc-chip-${section.id}`}
              className={cx(
                buttonClass(isActive ? 'primary' : 'secondary', 'sm'),
                // The chip row scrolls horizontally on narrow viewports, so the
                // active chip can sit off-screen. Same reason as the sidebar.
                isActive && 'scroll-mx-3',
              )}
            >
              {t(section.labelKey)}
            </a>
          );
        })}
      </nav>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[11rem_minmax(0,1fr)]">
        {/* xl+: sticky sidebar TOC. */}
        <aside className="hidden xl:block">
          <nav
            aria-label={t('accessDocs.toc.aria')}
            data-testid="docs-toc"
            className="sticky top-4 flex flex-col gap-0.5 rounded-lg border border-line-soft bg-card p-1.5"
          >
            {SECTIONS.map((section) => {
              const isActive = section.id === activeSection;
              return (
                <a
                  key={section.id}
                  href={`#${section.id}`}
                  // Same active affordance as the shell's own nav
                  // (`bg-signal-dim text-signal` + a lamp) — one convention for
                  // "you are here" across the console, learned once.
                  aria-current={isActive ? 'true' : undefined}
                  data-testid={`docs-toc-item-${section.id}`}
                  className={cx(
                    'flex items-center gap-2 rounded px-2.5 py-1.5 text-dense font-medium transition-colors duration-120 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/40',
                    isActive
                      ? 'bg-signal-dim text-signal'
                      : 'text-paper-mid hover:bg-raised hover:text-paper',
                  )}
                >
                  <span className="min-w-0 flex-1 truncate">{t(section.labelKey)}</span>
                  {isActive ? <StatusLamp tone="signal" size="sm" /> : null}
                </a>
              );
            })}
          </nav>
        </aside>

        <div className="flex min-w-0 flex-col gap-4">
          {/* ── Prepare ─────────────────────────────────────────── */}
          <DocsSection id="prepare" title={t('accessDocs.section.prepare.title')}>
            <KeyValueList
              rows={[
                { label: t('accessDocs.prepare.console'), value: consoleOrigin, mono: true },
                {
                  label: t('accessDocs.prepare.keys_page'),
                  value: `${consoleOrigin}/access/keys`,
                  mono: true,
                },
                { label: t('accessDocs.prepare.models'), value: 'GET /v1/models', mono: true },
                {
                  label: t('accessDocs.prepare.requests'),
                  value: `${consoleOrigin}/requests`,
                  mono: true,
                },
              ]}
            />
            <Callout tone="warn">{t('accessDocs.prepare.warning')}</Callout>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <MappingCard
                supplier="anthropic"
                channel={t('accessDocs.prepare.anthropic_entry')}
                models={claudeModels}
                fit={t('accessDocs.prepare.anthropic_fit')}
                testId="docs-mapping-anthropic"
              />
              <MappingCard
                supplier="openai"
                channel={t('accessDocs.prepare.openai_entry')}
                models={gptModels}
                fit={t('accessDocs.prepare.openai_fit')}
                testId="docs-mapping-openai"
              />
            </div>
            <SnippetCard
              title={t('accessDocs.prepare.env_check')}
              code={ENV_CHECK_SNIPPET}
              copyLabel={t('accessDocs.copy')}
              onCopy={onCopy(ENV_CHECK_SNIPPET)}
            />
          </DocsSection>

          {/* ── Model names + mappings (epic #898) ──────────────── */}
          <DocsSection id="models" title={t('accessDocs.section.models.title')}>
            <p className="m-0 text-dense leading-relaxed text-paper-mid">
              {t('accessDocs.models.intro', { example: exampleAnthropicModel })}
            </p>
            <KeyValueList
              rows={[
                {
                  label: t('accessDocs.models.row.canonical'),
                  value: t('accessDocs.models.row.canonical_value'),
                },
                {
                  label: t('accessDocs.models.row.alias'),
                  value: t('accessDocs.models.row.alias_value'),
                },
                {
                  label: t('accessDocs.models.row.manage'),
                  value: t('accessDocs.models.row.manage_value'),
                },
                {
                  label: t('accessDocs.models.row.pricing'),
                  value: t('accessDocs.models.row.pricing_value'),
                },
              ]}
            />
            <SnippetCard
              title={t('accessDocs.models.list_snippet')}
              code={modelsCurlSnippet(anthropicBase)}
              copyLabel={t('accessDocs.copy')}
              onCopy={onCopy(modelsCurlSnippet(anthropicBase))}
              testId="docs-snippet-models-curl"
            />
            <Callout>{t('accessDocs.models.source_of_truth')}</Callout>
            <Callout tone="warn">{t('accessDocs.models.cc_lane')}</Callout>
          </DocsSection>

          {/* ── Create key ──────────────────────────────────────── */}
          <DocsSection id="create-key" title={t('accessDocs.section.createKey.title')}>
            <ol className="m-0 grid list-none grid-cols-1 gap-3 p-0 md:grid-cols-3">
              <NumberedStep n={1} title={t('accessDocs.createKey.step1.title')}>
                {t('accessDocs.createKey.step1.body')}
              </NumberedStep>
              <NumberedStep n={2} title={t('accessDocs.createKey.step2.title')}>
                {t('accessDocs.createKey.step2.body')}
              </NumberedStep>
              <NumberedStep n={3} title={t('accessDocs.createKey.step3.title')}>
                {t('accessDocs.createKey.step3.body')}
              </NumberedStep>
            </ol>
            <KeyValueList
              rows={[
                {
                  label: t('accessDocs.createKey.row.name'),
                  value: t('accessDocs.createKey.row.name_value'),
                },
                {
                  label: t('accessDocs.createKey.row.template'),
                  value: t('accessDocs.createKey.row.template_value'),
                },
                {
                  label: t('accessDocs.createKey.row.limits'),
                  value: t('accessDocs.createKey.row.limits_value'),
                },
              ]}
            />
            <Callout>{t('accessDocs.createKey.hint')}</Callout>
            <Link
              to="/access/keys"
              className={cx(buttonClass('secondary'), 'w-fit')}
              data-testid="docs-open-keys"
            >
              {t('accessDocs.createKey.open_keys')}
            </Link>
          </DocsSection>

          {/* ── Claude Code ─────────────────────────────────────── */}
          <DocsSection id="claude-code" title={t('accessDocs.section.claudeCode.title')}>
            <p className="m-0 text-dense leading-relaxed text-paper-mid">
              {t('accessDocs.claudeCode.intro')}
            </p>
            <SnippetCard
              title={t('accessDocs.claudeCode.install')}
              code={CLAUDE_CODE_INSTALL_SNIPPET}
              copyLabel={t('accessDocs.copy')}
              onCopy={onCopy(CLAUDE_CODE_INSTALL_SNIPPET)}
            />
            <PlatformTabs value={platform} onChange={setPlatform} />
            <SnippetCard
              title={t('accessDocs.claudeCode.temp_env')}
              code={claudeTempEnvSnippet(platform, anthropicBase, exampleAnthropicModel)}
              copyLabel={t('accessDocs.copy')}
              onCopy={onCopy(claudeTempEnvSnippet(platform, anthropicBase, exampleAnthropicModel))}
              testId="docs-snippet-claude-temp-env"
            />
            <SnippetCard
              title={t('accessDocs.claudeCode.long_term')}
              code={claudeLongTermSnippet(platform, anthropicBase, exampleAnthropicModel)}
              copyLabel={t('accessDocs.copy')}
              onCopy={onCopy(claudeLongTermSnippet(platform, anthropicBase, exampleAnthropicModel))}
            />
            <KeyValueList
              rows={[
                {
                  label: t('accessDocs.claudeCode.row.model'),
                  value: t('accessDocs.claudeCode.row.model_value'),
                },
                {
                  label: t('accessDocs.claudeCode.row.first_test'),
                  value: t('accessDocs.claudeCode.row.first_test_value'),
                },
                {
                  label: t('accessDocs.claudeCode.row.prompt'),
                  value: t('accessDocs.claudeCode.row.prompt_value'),
                },
                {
                  label: t('accessDocs.claudeCode.row.url_mistake'),
                  value: t('accessDocs.claudeCode.row.url_mistake_value'),
                },
              ]}
            />
            <SnippetCard
              title={t('accessDocs.claudeCode.disable_betas')}
              code={disableClaudeBetasSnippet(platform)}
              copyLabel={t('accessDocs.copy')}
              onCopy={onCopy(disableClaudeBetasSnippet(platform))}
            />
          </DocsSection>

          {/* ── Codex ───────────────────────────────────────────── */}
          <DocsSection id="codex" title={t('accessDocs.section.codex.title')}>
            <p className="m-0 text-dense leading-relaxed text-paper-mid">
              {t('accessDocs.codex.intro', { example: exampleOpenaiModel })}
            </p>
            <Tabs
              value={protocol}
              onValueChange={(next) => {
                if (next === 'anthropic' || next === 'openai') setProtocol(next);
              }}
            >
              <TabsList ariaLabel={t('accessDocs.protocol.aria')}>
                <TabsTrigger value="anthropic" testId="docs-protocol-anthropic">
                  {t('accessDocs.protocol.anthropic')}
                </TabsTrigger>
                <TabsTrigger value="openai" testId="docs-protocol-openai">
                  {t('accessDocs.protocol.openai')}
                </TabsTrigger>
              </TabsList>
            </Tabs>
            <SnippetCard
              title={
                protocol === 'anthropic'
                  ? t('accessDocs.codex.env.claude')
                  : t('accessDocs.codex.env.codex')
              }
              code={
                protocol === 'anthropic'
                  ? claudeTempEnvSnippet(platform, anthropicBase, exampleAnthropicModel)
                  : codexEnvSnippet(platform, openaiBase)
              }
              copyLabel={t('accessDocs.copy')}
              onCopy={onCopy(
                protocol === 'anthropic'
                  ? claudeTempEnvSnippet(platform, anthropicBase, exampleAnthropicModel)
                  : codexEnvSnippet(platform, openaiBase),
              )}
              testId="docs-snippet-protocol-env"
            />
            <SnippetCard
              title="~/.codex/config.toml"
              code={codexConfigSnippet(openaiBase, exampleOpenaiModel)}
              copyLabel={t('accessDocs.copy')}
              onCopy={onCopy(codexConfigSnippet(openaiBase, exampleOpenaiModel))}
            />
            <KeyValueList
              rows={[
                {
                  label: t('accessDocs.codex.row.provider'),
                  value: t('accessDocs.codex.row.provider_value'),
                },
                { label: t('accessDocs.codex.row.base_url'), value: openaiBase, mono: true },
                {
                  label: t('accessDocs.codex.row.api_key'),
                  value: t('accessDocs.codex.row.api_key_value'),
                },
                { label: t('accessDocs.codex.row.model'), value: gptModels, mono: true },
              ]}
            />
          </DocsSection>

          {/* ── CC Switch ───────────────────────────────────────── */}
          <DocsSection id="cc-switch" title={t('accessDocs.section.ccSwitch.title')}>
            <p className="m-0 text-dense leading-relaxed text-paper-mid">
              {t('accessDocs.ccSwitch.intro')}
            </p>
            <SnippetCard
              title={t('accessDocs.ccSwitch.install')}
              code={CC_SWITCH_INSTALL_SNIPPET}
              copyLabel={t('accessDocs.copy')}
              onCopy={onCopy(CC_SWITCH_INSTALL_SNIPPET)}
            />
            <div className="grid grid-cols-1 gap-3 2xl:grid-cols-2">
              <ProviderPanel
                title={t('accessDocs.ccSwitch.claude_panel')}
                rows={[
                  {
                    label: t('accessDocs.ccSwitch.row.provider_name'),
                    value: t('accessDocs.ccSwitch.row.claude_provider_name'),
                  },
                  {
                    label: t('accessDocs.ccSwitch.row.app'),
                    value: t('accessDocs.ccSwitch.row.claude_app'),
                  },
                  {
                    label: t('accessDocs.ccSwitch.row.protocol'),
                    value: t('accessDocs.ccSwitch.row.claude_protocol'),
                  },
                  { label: t('accessDocs.ccSwitch.row.base_url'), value: anthropicBase, mono: true },
                  {
                    label: t('accessDocs.ccSwitch.row.api_key'),
                    value: t('accessDocs.ccSwitch.row.claude_key'),
                  },
                  { label: t('accessDocs.ccSwitch.row.model'), value: claudeModels, mono: true },
                ]}
              />
              <ProviderPanel
                title={t('accessDocs.ccSwitch.openai_panel')}
                rows={[
                  {
                    label: t('accessDocs.ccSwitch.row.provider_name'),
                    value: t('accessDocs.ccSwitch.row.openai_provider_name'),
                  },
                  {
                    label: t('accessDocs.ccSwitch.row.app'),
                    value: t('accessDocs.ccSwitch.row.openai_app'),
                  },
                  {
                    label: t('accessDocs.ccSwitch.row.protocol'),
                    value: t('accessDocs.ccSwitch.row.openai_protocol'),
                  },
                  { label: t('accessDocs.ccSwitch.row.base_url'), value: openaiBase, mono: true },
                  {
                    label: t('accessDocs.ccSwitch.row.api_key'),
                    value: t('accessDocs.ccSwitch.row.openai_key'),
                  },
                  { label: t('accessDocs.ccSwitch.row.model'), value: gptModels, mono: true },
                ]}
              />
            </div>
            <Checklist
              title={t('accessDocs.ccSwitch.checklist.title')}
              items={[
                t('accessDocs.ccSwitch.checklist.1'),
                t('accessDocs.ccSwitch.checklist.2'),
                t('accessDocs.ccSwitch.checklist.3'),
                t('accessDocs.ccSwitch.checklist.4'),
                t('accessDocs.ccSwitch.checklist.5'),
              ]}
            />
          </DocsSection>

          {/* ── OpenClaw ────────────────────────────────────────── */}
          <DocsSection id="openclaw" title={t('accessDocs.section.openclaw.title')}>
            <p className="m-0 text-dense leading-relaxed text-paper-mid">
              {t('accessDocs.openclaw.intro')}
            </p>
            <SnippetCard
              title={t('accessDocs.openclaw.snippet')}
              code={openclawProviderSnippet(openaiBase, exampleOpenaiModel)}
              copyLabel={t('accessDocs.copy')}
              onCopy={onCopy(openclawProviderSnippet(openaiBase, exampleOpenaiModel))}
            />
            <KeyValueList
              rows={[
                { label: t('accessDocs.openclaw.row.base_url'), value: openaiBase, mono: true },
                {
                  label: t('accessDocs.openclaw.row.api_key'),
                  value: t('accessDocs.openclaw.row.api_key_value'),
                },
                { label: t('accessDocs.openclaw.row.model'), value: gptModels, mono: true },
                {
                  label: t('accessDocs.openclaw.row.verify'),
                  value: t('accessDocs.openclaw.row.verify_value'),
                },
              ]}
            />
          </DocsSection>

          {/* ── Graphical clients ───────────────────────────────── */}
          <DocsSection id="clients" title={t('accessDocs.section.clients.title')}>
            <Table testId="docs-tools-table">
              <THead>
                <Tr interactive={false}>
                  <Th>{t('accessDocs.clients.column.tool')}</Th>
                  <Th>{t('accessDocs.clients.column.vendor')}</Th>
                  <Th>{t('accessDocs.clients.column.base')}</Th>
                  <Th>{t('accessDocs.clients.column.notes')}</Th>
                </Tr>
              </THead>
              <TBody>
                {TOOL_GUIDES.map((tool) => (
                  <Tr key={tool.name} interactive={false}>
                    <Td emphasis heading>{tool.name}</Td>
                    <Td mono label={t('accessDocs.clients.column.vendor')}>
                      {tool.supplier === 'mixed' ? 'anthropic / openai' : tool.supplier}
                    </Td>
                    <Td mono label={t('accessDocs.clients.column.base')} className="max-w-56 truncate max-md:max-w-none">
                      {tool.base === 'both'
                        ? t('accessDocs.clients.base_both')
                        : tool.base === 'anthropic'
                          ? anthropicBase
                          : openaiBase}
                    </Td>
                    <Td label={t('accessDocs.clients.column.notes')} stack>{t(tool.detailKey)}</Td>
                  </Tr>
                ))}
              </TBody>
            </Table>
            <div className="grid grid-cols-1 gap-3 2xl:grid-cols-3">
              {CLIENT_GUIDES.map((guide) => (
                <article
                  key={guide.name}
                  className="rounded-lg border border-line-soft bg-well p-4"
                  data-testid={`docs-client-${guide.keyName}`}
                >
                  <h3 className="m-0 text-body font-medium text-paper">{guide.name}</h3>
                  <p className="m-0 mt-1 text-label text-paper-low">
                    {t('accessDocs.clients.key_name', { name: guide.keyName })}
                  </p>
                  <KeyValueList
                    compact
                    rows={[
                      { label: t('accessDocs.clients.row.entry'), value: t(guide.entryKey) },
                      {
                        label: t('accessDocs.clients.row.provider_type'),
                        value: t('accessDocs.clients.row.provider_type_value'),
                      },
                      { label: t('accessDocs.clients.row.api_host'), value: openaiBase, mono: true },
                      { label: t('accessDocs.clients.row.model'), value: gptModels, mono: true },
                      { label: t('accessDocs.clients.row.test'), value: t(guide.testKey) },
                    ]}
                  />
                  <p className="m-0 mt-3 text-label leading-relaxed text-paper-low">
                    {t(guide.noteKey)}
                  </p>
                </article>
              ))}
            </div>
            <SnippetCard
              title={t('accessDocs.clients.lobechat_env')}
              code={lobechatEnvSnippet(openaiBase)}
              copyLabel={t('accessDocs.copy')}
              onCopy={onCopy(lobechatEnvSnippet(openaiBase))}
            />
          </DocsSection>

          {/* ── Claude Desktop ──────────────────────────────────── */}
          <DocsSection id="claude-desktop" title={t('accessDocs.section.claudeDesktop.title')}>
            <p className="m-0 text-dense leading-relaxed text-paper-mid">
              {t('accessDocs.claudeDesktop.intro')}
            </p>
            <div className="grid grid-cols-1 gap-3 2xl:grid-cols-2">
              <Checklist
                title={t('accessDocs.claudeDesktop.macos.title')}
                items={[
                  t('accessDocs.claudeDesktop.macos.1'),
                  t('accessDocs.claudeDesktop.macos.2'),
                  t('accessDocs.claudeDesktop.macos.3'),
                  t('accessDocs.claudeDesktop.macos.4', { baseUrl: anthropicBase }),
                  t('accessDocs.claudeDesktop.macos.5'),
                ]}
              />
              <Checklist
                title={t('accessDocs.claudeDesktop.windows.title')}
                items={[
                  t('accessDocs.claudeDesktop.windows.1'),
                  t('accessDocs.claudeDesktop.windows.2'),
                  t('accessDocs.claudeDesktop.windows.3'),
                  t('accessDocs.claudeDesktop.windows.4', { baseUrl: anthropicBase }),
                  t('accessDocs.claudeDesktop.windows.5'),
                ]}
              />
            </div>
            <KeyValueList
              rows={[
                {
                  label: t('accessDocs.claudeDesktop.row.no_menu'),
                  value: t('accessDocs.claudeDesktop.row.no_menu_value'),
                },
                {
                  label: t('accessDocs.claudeDesktop.row.no_gateway'),
                  value: t('accessDocs.claudeDesktop.row.no_gateway_value'),
                },
                {
                  label: t('accessDocs.claudeDesktop.row.no_connect'),
                  value: t('accessDocs.claudeDesktop.row.no_connect_value'),
                },
                {
                  label: t('accessDocs.claudeDesktop.row.rejected'),
                  value: t('accessDocs.claudeDesktop.row.rejected_value'),
                },
              ]}
            />
          </DocsSection>

          {/* ── VS Code ─────────────────────────────────────────── */}
          <DocsSection id="vscode" title={t('accessDocs.section.vscode.title')}>
            <p className="m-0 text-dense leading-relaxed text-paper-mid">
              {t('accessDocs.vscode.intro')}
            </p>
            <SnippetCard
              title={t('accessDocs.vscode.snippet')}
              code={claudeTempEnvSnippet(platform, anthropicBase, exampleAnthropicModel)}
              copyLabel={t('accessDocs.copy')}
              onCopy={onCopy(claudeTempEnvSnippet(platform, anthropicBase, exampleAnthropicModel))}
            />
            <KeyValueList
              rows={[
                { label: t('accessDocs.vscode.row.openai_base'), value: openaiBase, mono: true },
                {
                  label: t('accessDocs.vscode.row.anthropic_base'),
                  value: anthropicBase,
                  mono: true,
                },
                {
                  label: t('accessDocs.vscode.row.openai_models'),
                  value: t('accessDocs.vscode.row.openai_models_value'),
                },
                {
                  label: t('accessDocs.vscode.row.anthropic_models'),
                  value: t('accessDocs.vscode.row.anthropic_models_value'),
                },
              ]}
            />
          </DocsSection>

          {/* ── Verify + troubleshoot ───────────────────────────── */}
          {/* ── Images ──────────────────────────────────────────
              The OpenAI Images protocol, served by an upstream whose only
              image capability is a built-in tool of its Responses endpoint.
              That asymmetry is invisible to a caller — the request and
              response are the standard shapes — but three of its consequences
              are NOT, and a tenant who does not know them reads correct
              behaviour as a bug: `size` and `quality` are advisory, one call
              yields one image, and the model may decline. Documented here
              rather than discovered in production. */}
          <DocsSection id="images" title={t('accessDocs.section.images.title')}>
            <Callout>{t('accessDocs.images.note')}</Callout>
            {/* Both snippets carry generate AND edit, so the two cards are the
                same two calls in two languages — equal weight, and the split
                is the reader's choice of tool rather than a split of the
                feature across cards. */}
            <div className="grid grid-cols-1 gap-3 2xl:grid-cols-2">
              <SnippetCard
                title={t('accessDocs.images.sdk')}
                code={imagesSdkSnippet(openaiBase, exampleImageModel)}
                copyLabel={t('accessDocs.copy')}
                onCopy={onCopy(imagesSdkSnippet(openaiBase, exampleImageModel))}
                testId="docs-snippet-images-sdk"
              />
              <SnippetCard
                title={t('accessDocs.images.curl')}
                code={imagesCurlBothSnippet(openaiBase, exampleImageModel)}
                copyLabel={t('accessDocs.copy')}
                onCopy={onCopy(imagesCurlBothSnippet(openaiBase, exampleImageModel))}
                testId="docs-snippet-images-curl"
              />
            </div>
            <ImageParamTable
              caption={t('accessDocs.images.table.generate')}
              rows={IMAGE_GENERATE_PARAMS}
              t={t}
              testId="docs-images-generate-params-table"
            />
            <ImageParamTable
              caption={t('accessDocs.images.table.edit')}
              rows={IMAGE_EDIT_PARAMS}
              t={t}
              testId="docs-images-edit-params-table"
            />
            <Callout>{t('accessDocs.images.billing')}</Callout>
          </DocsSection>

          <DocsSection id="verify" title={t('accessDocs.section.verify.title')}>
            <Callout>{t('accessDocs.verify.note')}</Callout>
            <div className="grid grid-cols-1 gap-3 2xl:grid-cols-2">
              {/* The Anthropic example keeps a placeholder model on purpose:
                  the catalog carries no marker for which Claude namespaces
                  accept plain-API traffic, so any concrete default could 5xx
                  under curl (Claude Code-only namespaces). */}
              <SnippetCard
                title={t('accessDocs.verify.anthropic_curl')}
                code={anthropicCurlSnippet(anthropicBase)}
                copyLabel={t('accessDocs.copy')}
                onCopy={onCopy(anthropicCurlSnippet(anthropicBase))}
                testId="docs-snippet-anthropic-curl"
              />
              <SnippetCard
                title={t('accessDocs.verify.openai_curl')}
                code={openaiCurlSnippet(openaiBase, exampleOpenaiModel)}
                copyLabel={t('accessDocs.copy')}
                onCopy={onCopy(openaiCurlSnippet(openaiBase, exampleOpenaiModel))}
              />
            </div>
            <Table testId="docs-errors-table">
              <THead>
                <Tr interactive={false}>
                  <Th>{t('accessDocs.verify.column.symptom')}</Th>
                  <Th>{t('accessDocs.verify.column.reason')}</Th>
                  <Th>{t('accessDocs.verify.column.action')}</Th>
                </Tr>
              </THead>
              <TBody>
                {VERIFY_ERRORS.map((row) => (
                  <Tr key={row.symptomKey} interactive={false}>
                    <Td emphasis heading>{t(row.symptomKey)}</Td>
                    <Td label={t('accessDocs.verify.column.reason')} stack>{t(row.reasonKey)}</Td>
                    <Td label={t('accessDocs.verify.column.action')} stack>{t(row.actionKey)}</Td>
                  </Tr>
                ))}
              </TBody>
            </Table>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <ActionLink
                to="/usage"
                label={t('accessDocs.verify.link.usage')}
                detail={t('accessDocs.verify.link.usage_detail')}
                testId="docs-link-usage"
              />
              <ActionLink
                to="/requests"
                label={t('accessDocs.verify.link.requests')}
                detail={t('accessDocs.verify.link.requests_detail')}
                testId="docs-link-requests"
              />
              <ActionLink
                to="/billing/pricing"
                label={t('accessDocs.verify.link.pricing')}
                detail={t('accessDocs.verify.link.pricing_detail')}
                testId="docs-link-pricing"
              />
            </div>
            <Callout>{t('accessDocs.verify.security')}</Callout>
          </DocsSection>
        </div>
      </div>
    </section>
  );
}

/* ── Local presentational helpers (Deck tokens only) ─────────────── */

/**
 * One route's parameter table. Shared by `generations` and `edits` so the two
 * cannot drift into different column sets — the whole reason for splitting
 * them was that a merged table hid which parameter belonged to which route.
 */
function ImageParamTable({
  caption,
  rows,
  t,
  testId,
}: {
  readonly caption: string;
  readonly rows: readonly ImageParamRow[];
  readonly t: (key: I18nKey) => string;
  readonly testId: string;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-2">
      <p className="m-0 text-dense font-medium text-paper">{caption}</p>
      <Table testId={testId}>
        <THead>
          <Tr interactive={false}>
            <Th>{t('accessDocs.images.column.param')}</Th>
            <Th>{t('accessDocs.images.column.required')}</Th>
            <Th>{t('accessDocs.images.column.default')}</Th>
            <Th>{t('accessDocs.images.column.behaviour')}</Th>
          </Tr>
        </THead>
        <TBody>
          {rows.map((row) => (
            <Tr key={row.param} interactive={false}>
              <Td emphasis heading mono>{row.param}</Td>
              <Td label={t('accessDocs.images.column.required')} stack>
                {row.required ? t('accessDocs.images.required.yes') : t('accessDocs.images.required.no')}
              </Td>
              <Td label={t('accessDocs.images.column.default')} mono={row.defaultValue !== undefined} stack>
                {row.defaultValue ?? t('accessDocs.images.default.none')}
              </Td>
              <Td label={t('accessDocs.images.column.behaviour')} stack>
                {t(row.behaviourKey)}
              </Td>
            </Tr>
          ))}
        </TBody>
      </Table>
    </div>
  );
}

function DocsSection({
  id,
  title,
  children,
}: {
  readonly id: SectionId;
  readonly title: string;
  readonly children: ReactNode;
}): JSX.Element {
  return (
    <div id={id} className="scroll-mt-4" data-testid={`docs-section-${id}`}>
      <Panel caption={title} padded>
        <div className="flex flex-col gap-4">{children}</div>
      </Panel>
    </div>
  );
}

function FactCard({
  title,
  note,
  noteTone = 'neutral',
  value,
  copyLabel,
  onCopy,
  testId,
}: {
  readonly title: string;
  readonly note: string;
  readonly noteTone?: 'neutral' | 'warn' | 'signal';
  readonly value: string;
  readonly copyLabel?: string;
  readonly onCopy?: () => void;
  readonly testId?: string;
}): JSX.Element {
  const noteClass =
    noteTone === 'warn'
      ? 'border-warn-line text-warn'
      : noteTone === 'signal'
        ? 'border-signal-deep/50 text-signal'
        : 'border-line text-paper-low';
  return (
    <article data-testid={testId} className="min-w-0 rounded-lg border border-line-soft bg-card p-4">
      <div className="flex items-start justify-between gap-3">
        <h2 className="m-0 text-body font-medium text-paper">{title}</h2>
        <span className={cx('whitespace-nowrap rounded-full border px-2 py-0.5 text-micro', noteClass)}>
          {note}
        </span>
      </div>
      <div className="mt-3 flex items-center justify-between gap-2">
        <p className="m-0 min-w-0 overflow-x-auto whitespace-nowrap font-mono text-dense text-paper-mid">
          {value}
        </p>
        {onCopy !== undefined && copyLabel !== undefined ? (
          <Button variant="ghost" size="sm" onClick={onCopy} data-testid={testId !== undefined ? `${testId}-copy` : undefined}>
            <Icon name="content_copy" size="xs" />
            {copyLabel}
          </Button>
        ) : null}
      </div>
    </article>
  );
}

function SnippetCard({
  title,
  code,
  copyLabel,
  onCopy,
  testId,
}: {
  readonly title: string;
  readonly code: string;
  readonly copyLabel: string;
  readonly onCopy: () => void;
  readonly testId?: string;
}): JSX.Element {
  return (
    <article data-testid={testId} className="min-w-0 overflow-hidden rounded-lg border border-line-soft bg-well">
      <div className="flex items-center justify-between gap-3 border-b border-line-soft px-3 py-1.5">
        <h3 className="m-0 text-label font-medium text-paper-mid">{title}</h3>
        <Button variant="ghost" size="sm" onClick={onCopy}>
          <Icon name="content_copy" size="xs" />
          {copyLabel}
        </Button>
      </div>
      <pre className="m-0 overflow-x-auto px-3 py-2.5 font-mono text-dense leading-relaxed text-paper">
        {code}
      </pre>
    </article>
  );
}

function KeyValueList({
  rows,
  compact = false,
}: {
  readonly rows: readonly { readonly label: string; readonly value: string; readonly mono?: boolean }[];
  readonly compact?: boolean;
}): JSX.Element {
  return (
    <dl
      className={cx(
        'm-0 divide-y divide-line-soft',
        compact ? 'mt-3' : 'rounded-lg border border-line-soft',
      )}
    >
      {rows.map((row) => (
        <div
          key={`${row.label}:${row.value}`}
          className="grid grid-cols-1 gap-1 px-3 py-2 md:grid-cols-[9rem_minmax(0,1fr)]"
        >
          <dt className="text-label text-paper-low">{row.label}</dt>
          <dd
            className={cx(
              'm-0 min-w-0 break-words text-dense text-paper-mid',
              row.mono === true && 'font-mono',
            )}
          >
            {row.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function Callout({
  children,
  tone = 'neutral',
}: {
  readonly children: ReactNode;
  readonly tone?: 'neutral' | 'warn';
}): JSX.Element {
  return (
    <div
      className={cx(
        'rounded-lg border px-4 py-3 text-dense leading-relaxed',
        tone === 'warn' ? 'border-warn-line bg-warn-dim/30 text-warn' : 'border-line-soft bg-well text-paper-mid',
      )}
    >
      {children}
    </div>
  );
}

function NumberedStep({
  n,
  title,
  children,
}: {
  readonly n: number;
  readonly title: string;
  readonly children: ReactNode;
}): JSX.Element {
  return (
    <li className="flex gap-3 rounded-lg border border-line-soft bg-well p-3">
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded bg-raised font-mono text-label text-signal">
        {n}
      </span>
      <span className="min-w-0">
        <strong className="block text-dense font-medium text-paper">{title}</strong>
        <span className="text-dense leading-relaxed text-paper-mid">{children}</span>
      </span>
    </li>
  );
}

function MappingCard({
  supplier,
  channel,
  models,
  fit,
  testId,
}: {
  readonly supplier: string;
  readonly channel: string;
  readonly models: string;
  readonly fit: string;
  readonly testId?: string;
}): JSX.Element {
  return (
    <article data-testid={testId} className="rounded-lg border border-line-soft bg-well p-4">
      <div className="flex items-center justify-between gap-3">
        <h3 className="m-0 font-mono text-body font-medium text-signal">{supplier}</h3>
        <span className="rounded-full border border-line px-2 py-0.5 text-micro text-paper-low">
          {channel}
        </span>
      </div>
      <p className="m-0 mt-3 text-dense leading-relaxed text-paper-mid">{fit}</p>
      <p className="m-0 mt-2 break-words font-mono text-label leading-relaxed text-paper-low">
        {models}
      </p>
    </article>
  );
}

function ProviderPanel({
  title,
  rows,
}: {
  readonly title: string;
  readonly rows: readonly { readonly label: string; readonly value: string; readonly mono?: boolean }[];
}): JSX.Element {
  return (
    <article className="rounded-lg border border-line-soft bg-well p-4">
      <h3 className="m-0 text-body font-medium text-paper">{title}</h3>
      <KeyValueList compact rows={rows} />
    </article>
  );
}

function Checklist({
  title,
  items,
}: {
  readonly title: string;
  readonly items: readonly string[];
}): JSX.Element {
  return (
    <div className="rounded-lg border border-line-soft bg-well p-4">
      <h3 className="m-0 text-body font-medium text-paper">{title}</h3>
      <ol className="m-0 mt-3 flex list-none flex-col gap-2 p-0">
        {items.map((item, index) => (
          <li key={item} className="flex gap-2.5">
            <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded bg-raised font-mono text-micro text-signal">
              {index + 1}
            </span>
            <span className="text-dense leading-relaxed text-paper-mid">{item}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

function ActionLink({
  to,
  label,
  detail,
  testId,
}: {
  readonly to: string;
  readonly label: string;
  readonly detail: string;
  readonly testId?: string;
}): JSX.Element {
  return (
    <Link
      to={to}
      data-testid={testId}
      className="flex flex-col gap-1 rounded-lg border border-line-soft bg-card p-4 transition-colors duration-120 hover:border-line-strong hover:bg-raised focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/40"
    >
      <span className="text-dense font-medium text-paper">{label}</span>
      <span className="text-label text-paper-low">{detail}</span>
    </Link>
  );
}

function PlatformTabs({
  value,
  onChange,
}: {
  readonly value: DocsPlatform;
  readonly onChange: (next: DocsPlatform) => void;
}): JSX.Element {
  const t = useT();
  return (
    <Tabs
      value={value}
      onValueChange={(next) => {
        if (next === 'macos' || next === 'windows') onChange(next);
      }}
    >
      <TabsList ariaLabel={t('accessDocs.platform.aria')}>
        <TabsTrigger value="macos" testId="docs-platform-macos">
          {t('accessDocs.platform.macos')}
        </TabsTrigger>
        <TabsTrigger value="windows" testId="docs-platform-windows">
          {t('accessDocs.platform.windows')}
        </TabsTrigger>
      </TabsList>
    </Tabs>
  );
}
